import React from 'react';
import { motion } from 'framer-motion';
import { RiFocus3Line, RiLeafLine, RiSeedlingLine } from 'react-icons/ri';

const AboutSection = () => {
    const values = [
        {
            icon: RiLeafLine,
            title: "Our Mission",
            description: "To safeguard Earth's biodiversity through scientific research, community action, and ecological restoration."
        },
        {
            icon: RiFocus3Line,
            title: "Our Vision",
            description: "A world where nature and humanity thrive in perfect harmony, supported by stable ecosystems and sustainable practices."
        },
        {
            icon: RiSeedlingLine,
            title: "Our Values",
            description: "Integrity in research, transparency in action, and a deep-rooted passion for the preservation of all living things."
        }
    ];

    return (
        <section className="py-20 bg-white overflow-hidden" id="about">
            <div className="mx-auto max-w-7xl px-6">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-24">

                    {/* Image Column */}
                    <div className="w-full lg:w-1/2 relative">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="relative z-10"
                        >
                            <img
                                src="https://images.unsplash.com/photo-1511497584788-876760111969?q=80&w=2070&auto=format&fit=crop"
                                alt="Cinematic Forest"
                                className="rounded-[40px] shadow-2xl relative z-10"
                            />

                            {/* Floating decorative card */}
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.5, duration: 0.6 }}
                                className="absolute -bottom-8 -right-8 bg-emerald-600 p-8 rounded-3xl shadow-xl z-20 hidden md:block"
                            >
                                <span className="text-4xl font-bold text-white block mb-1">15+</span>
                                <span className="text-emerald-100 text-sm font-bold uppercase tracking-widest">Years of Impact</span>
                            </motion.div>
                        </motion.div>

                        {/* Background elements */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[110%] bg-emerald-50 rounded-full blur-[100px] -z-10" />
                    </div>

                    {/* Content Column */}
                    <div className="w-full lg:w-1/2">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <span className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.4em] mb-4 block">
                                Who We Are
                            </span>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 sm:mb-8 font-display leading-[1.2] sm:leading-[1.1]">
                                Dedicated to the <span className="text-emerald-600 italic">Restoration</span> of Our Planet's Soul.
                            </h2>
                            <p className="text-base text-slate-500 mb-10 leading-relaxed font-sans">
                                Founded on the principles of ecological stewardship, we bridge the gap between scientific investigation and on-the-ground conservation. Our team of experts works tirelessly to protect endangered habitats and inspire a global movement toward environmental responsibility.
                            </p>

                            <div className="space-y-8">
                                {values.map((v, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.1 * i }}
                                        className="flex gap-4"
                                    >
                                        <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0 text-emerald-600">
                                            <v.icon size={20} />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-slate-900 mb-1 text-sm sm:text-base">{v.title}</h3>
                                            <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">{v.description}</p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AboutSection;
